/**
 * ポータル: 経費（船員アプリ）
 * 自分の立替経費・購買申請の提出と状況確認。領収書はスマホ写真を添付。
 */
import { useCallback, useEffect, useState } from 'react';
import { AppHeader } from '../../components/AppHeader';
import { PIcon } from '../../components/PIcon';
import { BottomNav } from '../../components/BottomNav';
import { apiFetch } from '../../services/api';
import { PillBadge, STATUS_PILL, fmtDateTime, fileUrl, inputStyle, labelStyle, EmptyState } from './ui';

interface Category { id: string; name: string }
interface Expense {
  id: string; expense_type: string; expense_date: string; amount: number;
  payee: string | null; category_name?: string | null; description: string | null;
  receipt_path: string | null; status: string; status_label: string; reject_reason: string | null;
  created_at: string;
  events?: Array<{ action: string; actor_name: string | null; created_at: string }>;
}

const EVENT_LABEL: Record<string, string> = {
  create: '申請', update: '修正', receipt_replace: '領収書差替', approve: '承認', reject: '却下', settle: '精算', withdraw: '取下げ',
};

export default function PortalExpensesPage() {
  const [tab, setTab] = useState<'list' | 'new'>('list');
  const [rows, setRows] = useState<Expense[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [detail, setDetail] = useState<Expense | null>(null);

  // 申請フォーム
  const [type, setType] = useState('expense');
  const [date, setDate] = useState(new Date(Date.now() + 9 * 3600 * 1000).toISOString().slice(0, 10));
  const [amount, setAmount] = useState('');
  const [payee, setPayee] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [description, setDescription] = useState('');
  const [receipt, setReceipt] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await apiFetch<{ data: Expense[] }>('/portal/expenses?mine=1'); setRows(r.data ?? []); } catch { /* noop */ }
  }, []);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    apiFetch<{ data: Category[] }>('/portal/expenses/categories').then(r => setCategories(r.data ?? [])).catch(() => { /* noop */ });
  }, []);

  const submit = async () => {
    const amt = parseInt(amount, 10);
    if (!date || !Number.isFinite(amt) || amt <= 0) { alert('取引日と金額を入力してください'); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append('expense_type', type);
      form.append('expense_date', date);
      form.append('amount', String(amt));
      if (payee) form.append('payee', payee);
      if (categoryId) form.append('category_id', categoryId);
      if (description) form.append('description', description);
      if (receipt) form.append('receipt', receipt);
      await apiFetch('/portal/expenses', { method: 'POST', body: form });
      setAmount(''); setPayee(''); setDescription(''); setReceipt(null);
      setTab('list');
      await load();
      alert('申請しました。承認されるまでお待ちください。');
    } catch (e) { alert(e instanceof Error ? e.message : '申請に失敗しました'); }
    finally { setBusy(false); }
  };

  const open = async (id: string) => {
    try { const r = await apiFetch<{ data: Expense }>(`/portal/expenses/${id}`); setDetail(r.data); }
    catch (e) { alert(e instanceof Error ? e.message : '取得に失敗しました'); }
  };

  const withdraw = async (e: Expense) => {
    if (!confirm('この申請を取り下げますか？')) return;
    try {
      await apiFetch(`/portal/expenses/${e.id}/action`, { method: 'POST', body: JSON.stringify({ action: 'withdraw' }) });
      setDetail(null);
      await load();
    } catch (err) { alert(err instanceof Error ? err.message : '失敗しました'); }
  };

  return (
    <div style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', background: '#f8fafc' }}>
      <AppHeader title="経費">
        <div style={{ display: 'flex', gap: 6, background: '#f1f5f9', borderRadius: 12, padding: 4 }}>
          {([['list', '自分の申請'], ['new', '新しく申請']] as const).map(([key, lbl]) => (
            <button key={key} onClick={() => setTab(key)}
              style={{
                flex: 1, padding: '8px 0', borderRadius: 9, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700,
                background: tab === key ? 'white' : 'transparent', color: tab === key ? '#0f172a' : '#64748b',
                boxShadow: tab === key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              }}>{lbl}</button>
          ))}
        </div>
      </AppHeader>

      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px 90px' }}>
        {tab === 'list' ? (
          rows.length === 0 ? (
            <EmptyState img="/portal/empty-expenses.png" text="経費の申請はまだありません" />
          ) : rows.map(e => {
            const pill = STATUS_PILL[e.status] ?? { color: '#334155', bg: '#f1f5f9' };
            return (
              <button key={e.id} onClick={() => void open(e.id)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', marginBottom: 10,
                  background: '#fff', border: '1.5px solid #e4e9f0', borderRadius: 14, padding: '13px 14px', cursor: 'pointer',
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>¥{e.amount.toLocaleString()}</span>
                  <PillBadge color={pill.color} bg={pill.bg}>{e.status_label}</PillBadge>
                  <span style={{ marginLeft: 'auto', fontSize: 11.5, color: '#64748b' }}>{e.expense_date.replace(/-/g, '/')}</span>
                </div>
                <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 3 }}>
                  {e.expense_type === 'purchase' ? '購買申請' : '立替精算'}
                  {e.payee && ` ・ ${e.payee}`}{e.category_name && ` ・ ${e.category_name}`}
                </div>
              </button>
            );
          })
        ) : (
          <div style={{ background: '#fff', border: '1.5px solid #e4e9f0', borderRadius: 16, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label style={labelStyle}>区分</label>
              <select style={inputStyle} value={type} onChange={e => setType(e.target.value)}>
                <option value="expense">立替経費の精算（支払い済み）</option>
                <option value="purchase">購買申請（買う前の相談）</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>取引日 *</label>
                <input type="date" style={inputStyle} value={date} onChange={e => setDate(e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>金額（円） *</label>
                <input type="number" inputMode="numeric" style={inputStyle} value={amount} onChange={e => setAmount(e.target.value)} placeholder="3500" />
              </div>
            </div>
            <div>
              <label style={labelStyle}>取引先（店名など）</label>
              <input style={inputStyle} value={payee} onChange={e => setPayee(e.target.value)} placeholder="例: ○○商店" />
            </div>
            <div>
              <label style={labelStyle}>科目</label>
              <select style={inputStyle} value={categoryId} onChange={e => setCategoryId(e.target.value)}>
                <option value="">選択してください</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>内容</label>
              <input style={inputStyle} value={description} onChange={e => setDescription(e.target.value)} placeholder="例: 作業用手袋の購入" />
            </div>
            <div>
              <label style={labelStyle}>領収書（写真でOK）</label>
              <input type="file" accept="image/*,.pdf" capture="environment" onChange={e => setReceipt(e.target.files?.[0] ?? null)} />
              {receipt && <div style={{ fontSize: 12, color: '#15803d', marginTop: 4 }}>✓ {receipt.name}</div>}
            </div>
            <button disabled={busy} onClick={() => void submit()}
              style={{ border: 'none', background: busy ? '#64748b' : '#0f766e', color: '#fff', borderRadius: 12, padding: '14px', fontSize: 15, fontWeight: 800, cursor: busy ? 'not-allowed' : 'pointer' }}>
              {busy ? '送信中...' : '申請する'}
            </button>
          </div>
        )}
      </div>

      {detail && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end' }}
          onClick={() => setDetail(null)}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: '18px 18px 0 0', width: '100%', maxHeight: '86dvh', overflowY: 'auto', padding: '18px 18px 28px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 18, fontWeight: 800 }}>¥{detail.amount.toLocaleString()}</span>
              {(() => { const p = STATUS_PILL[detail.status] ?? { color: '#334155', bg: '#f1f5f9' }; return <PillBadge color={p.color} bg={p.bg}>{detail.status_label}</PillBadge>; })()}
              <button onClick={() => setDetail(null)} style={{ marginLeft: 'auto', border: 'none', background: 'none', fontSize: 18, color: '#64748b', cursor: 'pointer' }}>✕</button>
            </div>
            {[
              ['区分', detail.expense_type === 'purchase' ? '購買申請' : '立替精算'],
              ['取引日', detail.expense_date.replace(/-/g, '/')],
              ['取引先', detail.payee ?? '—'],
              ['科目', detail.category_name ?? '—'],
              ['内容', detail.description ?? '—'],
            ].map(([l, v]) => (
              <div key={l} style={{ display: 'flex', fontSize: 13.5, padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ width: 80, color: '#64748b', flexShrink: 0 }}>{l}</span>
                <span style={{ color: '#0f172a' }}>{v}</span>
              </div>
            ))}
            {detail.reject_reason && (
              <div style={{ fontSize: 13, color: '#dc2626', marginTop: 8 }}>却下理由: {detail.reject_reason}</div>
            )}
            {detail.receipt_path && (
              <a href={fileUrl(`/portal/expenses/${detail.id}/receipt`)} target="_blank" rel="noreferrer"
                style={{ display: 'block', fontSize: 13.5, color: '#0f766e', fontWeight: 700, marginTop: 10 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><PIcon name="paperclip" size={13} /> 領収書を表示</span>
              </a>
            )}
            {(detail.events?.length ?? 0) > 0 && (
              <div style={{ marginTop: 12 }}>
                {detail.events!.map((ev, i) => (
                  <div key={i} style={{ fontSize: 12, color: '#64748b', padding: '3px 0' }}>
                    {fmtDateTime(ev.created_at)}　{EVENT_LABEL[ev.action] ?? ev.action}（{ev.actor_name}）
                  </div>
                ))}
              </div>
            )}
            {detail.status === 'pending' && (
              <button onClick={() => void withdraw(detail)}
                style={{ width: '100%', marginTop: 14, border: '1.5px solid #fecaca', background: '#fef2f2', color: '#dc2626', borderRadius: 12, padding: '12px', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>
                申請を取り下げる
              </button>
            )}
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}
